/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DialProfileSelect.tsx
 🧩 Description:
    Searchable select dropdown for choosing Dial Profiles.
 ────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Search, Sliders } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../config/api';

interface DialProfile {
  id: number;
  name: string;
  description?: string;
}

interface DialProfileSelectProps {
  value: number | null;
  onChange: (value: number | null) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  allowNull?: boolean;
  nullLabel?: string;
}

export const DialProfileSelect: React.FC<DialProfileSelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select Dial Profile...',
  disabled = false,
  allowNull = true,
  nullLabel = 'None (no profile)'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState<DialProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load profiles on mount
  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/telephony/dial-profiles?limit=100&sortBy=name&sortOrder=asc'), {
        headers: getApiHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.data || []);
      }
    } catch (error) {
      console.error('Error loading Dial Profiles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return profiles;
    }
    
    const query = searchQuery.toLowerCase();
    return profiles.filter(profile => 
      profile.name.toLowerCase().includes(query) ||
      (profile.description && profile.description.toLowerCase().includes(query))
    );
  }, [searchQuery, profiles]);

  // Get label for selected value
  const selectedLabel = useMemo(() => {
    if (value === null) {
      return nullLabel;
    }
    const selected = profiles.find(profile => profile.id === value);
    return selected ? selected.name : placeholder;
  }, [value, profiles, placeholder, nullLabel]);

  // Handle selection
  const handleSelect = (profileId: number | null) => {
    onChange(profileId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Sliders className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                Loading profiles...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No profiles found
              </div>
            ) : (
              <>
                {allowNull && (
                  <button
                    type="button"
                    onClick={() => handleSelect(null)}
                    className={`
                      w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                      flex items-center gap-2
                      ${value === null ? 'bg-primary/10 text-primary' : 'text-foreground'}
                    `}
                  >
                    <Sliders className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{nullLabel}</span>
                  </button>
                )}
                {filteredOptions.map((profile) => (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => handleSelect(profile.id)}
                    className={`
                      w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                      ${value === profile.id ? 'bg-primary/10 text-primary' : 'text-foreground'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <Sliders className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{profile.name}</div>
                        {profile.description && (
                          <div className="truncate text-xs text-muted-foreground">
                            {profile.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer with count */}
          {!isLoading && filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} profile{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default DialProfileSelect;
